import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import moment from 'moment';
import qs from 'qs';

import { ADMIN_ACCESS_TOKEN_KEY, fetcher } from 'App';
import HistoryDetailInfo from '@components/molecules/HistoryDetailInfo';
import HistoryDetailStatistics from '../HistoryDetailStatistics';
import HistoryDetailTodos from '../HistoryDetailTodos';
import { IHistoryDetailStatistics, IHistoryInfo, StatisticsItem } from './types';
import SwitchMini from '@components/atoms/SwitchMini';
// import queryString from 'querystring';
import { StyledBackButton, StyledDetailHeader, StyledTabs, StyledTabsWrapper, StyledTitle } from './styles';

const HistoryDetailPage = () => {
  const params = useParams();
  const navigate = useNavigate();
  const [isAllLicense, setIsAllLicense] = useState(false);
  const [tab, setTab] = useState<'statistics' | 'details'>('statistics');
  const [accessToken, setAccessToken] = useState<string | null>(null);

  // statistics state 영역
  const [statisticsDateRange, setStatisticsDateRange] = useState<any>([
    {
      key: 'selection',
      startDate: undefined,
      endDate: undefined,
    },
  ]);

  const [rangeLimit, setRangeLimit] = useState<{ min: Date | undefined; max: Date | undefined }>({
    min: undefined,
    max: undefined,
  });

  const [statisticsItems, setStatisticsItems] = useState<StatisticsItem[]>([]);
  const [total, setTotal] = useState(0);
  const [licenseFromDate, setLicenseFromDate] = useState<string | undefined>(undefined);
  const [licenseToDate, setLicenseToDate] = useState<string | undefined>(undefined);
  const [isLoaded, setIsLoaded] = useState(false);
  // statistics state 영역

  const url = useMemo(() => {
    if (!params.id) return null;
    return `api/license/${params.id}`;
  }, [params]);

  const { data, error } = useSWR<IHistoryInfo>(url && accessToken ? [{ url, accessToken }] : null, {
    onSuccess(data) {
      // 최초 1회 만 실행
      if (isLoaded) return;

      let toDate = undefined;

      toDate = getIsAfterDateToToday(data.result.expirationDate);

      const range = {
        from: moment(data.result.fromDate).toDate(),
        to: moment(toDate).toDate(),
      };

      setRangeLimit({
        min: range.from,
        max: range.to,
      });

      setStatisticsDateRange([
        {
          key: 'selection',
          startDate: moment(range.from).toDate(),
          endDate: moment(range.to).toDate(),
        },
      ]);

      /**
       * License 의 from, to 를 임시 저장
       * All License 를 비활성화할 경우 다시 이전 데이터로 돌아가야 한다.
       */
      setLicenseFromDate(data.result.fromDate);
      setLicenseToDate(toDate);

      getStatisticsData(false, false, data.result.fromDate, toDate);

      // 최초 1회 load 동작을 위한 state update
      setIsLoaded(true);
    },
  });

  /**
   *
   * @param date
   * @returns
   */
  const getIsAfterDateToToday = (date: string | null) => {
    if (!date) return '';

    if (date === 'UNLIMITED') {
      date = moment().format('YYYY-MM-DD');
    } else if (moment(date).isAfter(moment())) {
      // 오늘 보다 날짜가 이후인 경우 오늘 기준으로 조회
      date = moment().format('YYYY-MM-DD');
    }

    return date;
  };

  /**
   * 통계 그래프 자료호출
   * @param isRangeReset
   * @param from
   * @param to
   * @returns
   */
  const getStatisticsData = async (
    isAllLicense = false,
    isRangeReset = false,
    from: string | undefined = undefined,
    to: string | undefined = undefined,
  ) => {
    if (!params.id || !accessToken) return;

    const fromDate = moment(from || licenseFromDate).format('YYYY-MM-DD');
    let toDate = moment(to || licenseToDate).format('YYYY-MM-DD');

    toDate = getIsAfterDateToToday(toDate);

    let query = isAllLicense && isRangeReset ? qs.stringify({ toDate }) : qs.stringify({ fromDate, toDate });

    // 요청 URL 정리
    let url = `api/license/${params.id}/statistic-history?` + query;
    if (isAllLicense) url = `api/license/statistic-history?` + query;

    const data = await fetcher<IHistoryDetailStatistics>({
      method: 'GET',
      url,
      accessToken,
    });

    const { status, result } = data;

    if (status === 'SUCCESS') {
      setTotal(result.total);

      setStatisticsItems(result.statisticsData.results);

      // 만약 range reset 이 필요한 경우 response 기반으로 범위설정
      if (isRangeReset) {
        let { fromDate, toDate } = result.statisticsData.condition;

        toDate = getIsAfterDateToToday(toDate);

        const startDate = moment(fromDate).toDate();
        const endDate = moment(toDate).toDate();

        setStatisticsDateRange([
          {
            key: 'selection',
            startDate,
            endDate,
          },
        ]);

        setRangeLimit({
          max: endDate,
          min: startDate,
        });
      }
    }
  };

  /**
   * 통계그래프 필터(달력) 선택시 동작
   * @param value
   */
  const dateHandler = (value: any) => {
    setStatisticsDateRange(value);

    const { startDate, endDate } = value[0];

    getStatisticsData(
      isAllLicense,
      false,
      moment(startDate).format('YYYY-MM-DD'),
      moment(endDate).format('YYYY-MM-DD'),
    );
  };

  const switchHandler = (value: boolean) => {
    if (tab === 'details') {
      setTab('statistics');
    }

    setIsAllLicense(value);
    getStatisticsData(value, true);
  };

  //
  const toDate = useMemo(() => {
    if (!data || !data.result) return undefined;
    return data.result.expirationDate === 'UNLIMITED' ? undefined : data.result.expirationDate;
  }, [data]);

  useEffect(function () {
    const newToken = localStorage.getItem(ADMIN_ACCESS_TOKEN_KEY);

    if (newToken) {
      setAccessToken(newToken);
    }
  }, []);

  if (error || (data && !data.result)) {
    return <div>failed to load</div>;
  }

  if (!params.id || !accessToken || !data) {
    return <div>loading...</div>;
  }

  return (
    <>
      <StyledDetailHeader>
        <StyledTitle>
          <StyledBackButton onClick={() => navigate(-1)} />
          <label className="detail-title">License History Detail</label>
          <SwitchMini label={`All License`} checked={isAllLicense} onChange={switchHandler} />
        </StyledTitle>

        {!isAllLicense ? (
          <HistoryDetailInfo
            date={data.result.expirationDate}
            user={data.result.numberOfUser}
            times={data.result.numberOfTimes}
          />
        ) : null}
      </StyledDetailHeader>

      {!isAllLicense ? (
        <StyledTabsWrapper>
          <StyledTabs tab={tab}>
            <li className={tab === 'statistics' ? 'active' : ''} onClick={() => setTab('statistics')}>
              Statistics
            </li>
            <li className={tab === 'details' ? 'active' : ''} onClick={() => setTab('details')}>
              Details
            </li>
          </StyledTabs>
        </StyledTabsWrapper>
      ) : null}

      {tab === 'statistics' ? (
        <HistoryDetailStatistics
          maxDate={rangeLimit.max}
          minDate={rangeLimit.min}
          selectedDate={statisticsDateRange}
          total={total}
          items={statisticsItems}
          onChangeDate={dateHandler}
        />
      ) : (
        <HistoryDetailTodos
          licenseId={params.id}
          accessToken={accessToken}
          defaultFromDate={data.result.fromDate}
          maxDate={toDate}
        />
      )}
    </>
  );
};

export default HistoryDetailPage;
